import { memo } from 'react';
import styled from 'styled-components';
import { Box, InlineButton, Text, theme, Tooltip } from '@frameio/vapor';
import { IconInfo } from '@frameio/vapor-icons';
import { NLP_SECTION_ID } from '../../../shared/types';
import type { QuickSearchResultSection } from '../../types';
import { BetaBadge } from '../shared';
import TEXT from './generated/TEXT/QuickSearchSectionHeader';

const SECTION_HEADER_HEIGHT = 36;

TEXT.DEFINE({
  /** Button label to show all results in a section */
  viewAll: 'Show all',
});

type QuickSearchSectionHeaderProps = {
  section: QuickSearchResultSection;
  onViewAllClick?: () => void;
  shouldShowViewAll: boolean;
};

export const QuickSearchSectionHeader = memo<QuickSearchSectionHeaderProps>(
  ({ section, onViewAllClick, shouldShowViewAll }) => {
    const showViewAllButton =
      shouldShowViewAll && section.hasMoreItems && onViewAllClick;

    return (
      <SectionHeaderContainer>
        <Box alignItems="center" display="flex" gap={4}>
          <Text color="tertiary" fontWeight="medium" variant="secondary">
            {section.title}
          </Text>

          {section?.tooltipText && (
            <Tooltip title={section.tooltipText}>
              <IconInfo color={theme.colors.icon.secondary} size={16} />
            </Tooltip>
          )}
        </Box>
        {showViewAllButton && (
          <InlineButton onPress={onViewAllClick} size="small" variant="primary">
            {TEXT.viewAll}
          </InlineButton>
        )}
        {section.id === NLP_SECTION_ID && <BetaBadge />}
      </SectionHeaderContainer>
    );
  }
);

const SectionHeaderContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  /** ensures the section header maintains the same height when the view all button is shown or hidden */
  min-height: ${SECTION_HEADER_HEIGHT}px;
  padding-bottom: ${theme.spaces[8]};
  padding-left: ${theme.spaces[12]};
  padding-right: ${theme.spaces[12]};
  padding-top: ${theme.spaces[8]};

  @media (min-width: ${theme.breakpoints.tablet}) {
    padding-right: 0;
    padding-left: ${theme.spaces[16]};
  }
`;
